import { CommerceList } from '@msdyn365-commerce/retail-proxy';
import { ICoreContext } from '@msdyn365-commerce/core';
import { removeLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CommerceListsDataActions.g';
import { useCallback } from 'react';
import { useProductEngagementTracker } from '../../../integrations/relewise/hooks/useProductEngagementTracker';

interface RemoveFromWishlistArgs {
    variantRecordId: number | undefined;
    relewiseProductId: string | undefined;
    relewiseVariantId: string | undefined;
}

interface RemoveFromWishlistResult {
    success: boolean;
    updatedList?: CommerceList;
}

export const useRemoveFromWishlist = (wishlist: CommerceList | undefined, context: ICoreContext, location: string) => {
    const { trackProductEngagement } = useProductEngagementTracker(context.request, context.telemetry);

    const logError = useCallback(
        (message: string) => {
            const fullMessage = `[${location}] ${message}`;
            console.error(fullMessage);
            context.telemetry.error(fullMessage);
        },
        [context.telemetry, location]
    );

    const removeFromWishlist = useCallback(
        async (args: RemoveFromWishlistArgs): Promise<RemoveFromWishlistResult> => {
            const { variantRecordId, relewiseProductId, relewiseVariantId } = args;

            if (variantRecordId === undefined || !relewiseProductId || !relewiseVariantId) {
                logError(
                    `Cannot remove item from wishlist: missing identifiers (variantRecordId: ${variantRecordId}, relewiseProductId: ${relewiseProductId}, relewiseVariantId: ${relewiseVariantId})`
                );
                return { success: false };
            }

            if (!wishlist || !wishlist.CommerceListLines) {
                logError('Cannot remove item from wishlist: no wishlist available');
                return { success: false };
            }

            const wishlistLine = wishlist.CommerceListLines.find(line => line.ProductId === variantRecordId);
            if (!wishlistLine) {
                logError(`Cannot remove item from wishlist: no line found for variant ${variantRecordId}`);
                return { success: false };
            }

            try {
                const updatedList = await removeLinesAsync({ callerContext: context.actionContext }, wishlist.Id, [wishlistLine]);
                trackProductEngagement(relewiseProductId, relewiseVariantId, false);
                return { success: true, updatedList };
            } catch (error) {
                logError(`Failed to remove item from wishlist: ${error instanceof Error ? error.message : String(error)}`);
                return { success: false };
            }
        },
        [context.actionContext, logError, trackProductEngagement, wishlist]
    );

    return removeFromWishlist;
};
